import React, { useRef, useState } from 'react'

import Button, { IconButton } from 'components/uiKit/Button'
import Dropdown from 'components/uiKit/Dropdown'
import type { IPlacement } from 'components/uiKit/Dropdown/types'
import Group from 'components/uiKit/Group'
import { KitSize, ValueType } from 'components/uiKit/KitTypes'
import Menu from 'components/uiKit/Menu'
import type { IMenuOption } from 'components/uiKit/Menu/MenuItem'
import { t } from 'services/Translation'
import { getEnumLabel } from 'utils/enum'

import * as s from './SortPopover.module.scss'

interface ISortPopoverProps<T extends ValueType> {
  name: string
  value?: T
  onChange: (value: T) => void
  size?: KitSize
  options: IMenuOption<T>[]
  placement?: IPlacement
  defaultValue?: T
  disabled?: boolean
  styleType?: 'default' | 'informative' | 'short'
  overlayStyle?: React.CSSProperties
}

const SortPopover = <T extends ValueType>({
  name,
  value,
  defaultValue,
  options,
  size,
  placement = 'bottom-start',
  onChange,
  disabled,
  styleType = 'default',
  overlayStyle,
}: ISortPopoverProps<T>) => {
  const navRef = useRef<HTMLDivElement>(null)
  const isActive = value && (defaultValue ? value.toString() !== defaultValue.toString() : true)
  const [showSort, setShowSort] = useState(false)

  const handleChange = (item: IMenuOption<T>) => {
    setShowSort(false)
    onChange(item.value as T)
  }

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowSort(false)
    onChange(null as T)
  }

  return (
    <Dropdown
      disabled={disabled}
      onVisibleChange={setShowSort}
      overlay={
        <Menu
          name={`${name}SortPopover`}
          onClick={handleChange}
          options={options}
          target={navRef.current}
          value={value}
        />
      }
      overlayStyle={overlayStyle}
      placement={placement}
      rootRef={navRef}
      styleType='clear'
      visible={showSort}
    >
      {styleType === 'short' ? (
        <IconButton
          active={showSort || isActive}
          icon='sort'
          name='sort'
          size={size}
          styleType='ghost'
        />
      ) : (
        <Group join>
          <Button
            active={(Boolean(isActive) || showSort) && styleType !== 'informative'}
            disabled={disabled}
            name={`${name}.sortButton`}
            size={size}
            styleType='ghost'
          >
            {styleType === 'informative' ? (
              <div className={s.informative}>
                {getEnumLabel('SortUiEnum', value || defaultValue || '') ||
                  value ||
                  defaultValue ||
                  t('uiKit.sortPopover.label')}
                <IconButton
                  icon='textDown'
                  name='textDown'
                  size={KitSize.XS}
                  styleType='secondary'
                  round
                />
              </div>
            ) : (
              t('uiKit.sortPopover.label')
            )}
          </Button>
          {isActive && styleType === 'default' && (
            <IconButton
              active={Boolean(isActive) || showSort}
              icon='otherClose'
              name='otherClose'
              onClick={handleReset}
              size={size}
              styleType='ghost'
            />
          )}
        </Group>
      )}
    </Dropdown>
  )
}

export default SortPopover
